import axios, { AxiosError, AxiosInstance } from "axios";

import { apiClient, ApiClient } from "./apiClient";
import { ApiResponse } from "./interceptors";
import { config } from "@/config";

export interface PaginatedResponse<T = unknown> {
  prev: boolean;
  next: boolean;
  page: number;
  item_per_page: number;
  total_page: number;
  total_count: number;
  history: T[];
}

// Staff Profile Types
export interface StaffProfile {
  _id: string;
  userId: string;
  bio?: string;
  profilePicture?: {
    fileType: string;
    fileUrl: string;
    size: number;
  };
  experience?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
  };
  dateOfBirth?: string;
  gender?: string;
  industry?: string;
  designation?: string;
  skills?: string[];
  website?: string;
  linkedin?: string;
  instagram?: string;
  resume?: {
    name?: string;
    fileType: string;
    fileUrl: string;
    size: number;
  };
  certifications?: {
    name: string;
    fileType: string;
    fileUrl: string;
    size: number;
  }[];
  ssn?: string;
  hourlyRate?: number;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  availableToHire?: boolean;
  createdAt: string;
  updatedAt: string;
}

// Manager Profile Types
export interface ManagerProfile {
  _id: string;
  userId: string;
  establishmentId: {
    _id: string;
    establishmentName: string;
  };
  bio?: string;
  profilePicture?: {
    fileType: string;
    fileUrl: string;
    size: number;
  };
  experience?: string;
  address?: any;
  gender?: string;
  birthDate?: string;
  createdAt: string;
  updatedAt: string;
}

// Manager interface (for Managers tab)
export interface Manager {
  _id: string;
  userId: string | {
    _id: string;
    fullName: string;
    email: string;
    phoneNumber?: string;
    profilePicture?: {
      fileUrl: string;
    };
    userStatus: number;
    role: number;
    createdAt?: string;
  };
  establishmentId: string | {
    _id: string;
    establishmentName: string;
    establishmentLogo?: {
      fileUrl: string;
    };
    serviceType: string;
  };
  bio?: string;
  profilePicture?: {
    fileType: string;
    fileUrl: string;
    size: number;
  };
  experience?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
  };
  gender?: string;
  birthDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

// User Types
export interface User {
  _id: string;
  fullName: string;
  email: string;
  role: number; // Numeric enum value from backend
  userStatus: number;
  profilePicture?: {
    fileUrl: string;
  };
  createdAt: string;
  updatedAt: string;
  // Additional populated fields from backend
  staffProfile?: StaffProfile;
  establishments?: Establishment[];
  managerProfile?: ManagerProfile;
}

// Admin Types
export interface Admin {
  _id: string;
  fullName: string;
  name?: string;
  email: string;
  role: string;
  createdAt: string;
  updatedAt: string;
}

// FAQ Types
export interface FAQ {
  _id: string;
  question: string;
  answer: string;
  createdAt: string;
  updatedAt: string;
}

// Content Types
export interface AppContent {
  _id: string;
  type: string;
  data: string;
  createdAt: string;
  updatedAt: string;
}

// Support Ticket Types
export interface SupportTicket {
  _id: string;
  subject: string;
  description: string;
  status: "open" | "in_progress" | "resolved" | "closed";
  priority: "low" | "medium" | "high" | "urgent";
  category: "technical" | "billing" | "account" | "general" | "bug" | "feature";
  userId: {
    _id: string;
    fullName: string;
    email: string;
    profilePicture?: string;
  };
  assignedTo?: {
    _id: string;
    fullName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
  closedAt?: string;
  messages: string[];
}

export interface SupportMessage {
  _id: string;
  ticketId: string;
  senderId: {
    _id: string;
    fullName: string;
    email: string;
    profilePicture?: string;
  };
  message: string;
  isInternal: boolean;
  attachments?: {
    fileName: string;
    fileUrl: string;
    fileType: string;
    fileSize: number;
  }[];
  createdAt: string;
  updatedAt: string;
}

export interface SupportStats {
  total: number;
  open: number;
  inProgress: number;
  resolved: number;
  closed: number;
}

// Subscription Plan Types
export interface SubscriptionPlan {
  _id: string;
  name: string;
  discountPriceTag: number;
  price: number;
  maxGigs: number;
  benefits: string[];
  stripePriceId?: string;
  stripeProductId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionAnalytics {
  totalRevenue: number;
  monthlyRecurringRevenue: number;
  revenueGrowth: number;
  activeSubscriptions: number;
  subscriptionGrowth: number;
  averageRevenuePerUser: number;
  churnRate: number;
  planDistribution: {
    basic: number;
    premium: number;
    enterprise: number;
  };
  topPerformingPlans: {
    name: string;
    revenue: number;
    subscribers: number;
    growth: number;
  }[];
}

export interface AdminDashboardData {
  overview: {
    totalUsers: number;
    totalEstablishments: number;
    totalJobs: number;
    totalApplications: number;
    totalRevenue: number;
    activeUsers: number;
    userGrowth: number;
    revenueGrowth: number;
    jobGrowth: number;
  };
  recentActivity: {
    recentUsers: {
      id: string;
      name: string;
      email: string;
      createdAt: string;
      userType: string;
    }[];
  };
  charts: {
    monthlyUsers: {
      month: string;
      users: number;
    }[];
  };
  support: {
    total: number;
    open: number;
    inProgress: number;
    resolved: number;
    closed: number;
  };
  subscriptions: SubscriptionAnalytics;
  activities: {
    id: string;
    type: "user_login" | "job_posted" | "payment_received" | "user_registered" | "system_alert";
    message: string;
    timestamp: string;
    severity: "info" | "warning" | "error" | "success";
    userId?: string;
    details?: string;
  }[];
}

// Notification Types
export interface Notification {
  _id: string;
  title: string;
  message: string;
  type: string;
  targetUsers: string[];
  sentAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationTemplate {
  _id: string;
  title: string;
  body: string;
  document?: string;
  category?: string;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationGroup {
  _id: string;
  groupName: string;
  userIds: string[];
  groupImg?: string | {
    fileName?: string;
    fileType?: string;
    fileUrl: string;
    size?: number;
  };
  createdAt: string;
  updatedAt: string;
}

// Statistics Types
export interface GraphData {
  month: string;
  parent: number;
  childCount: number;
}

// Industry Types
export interface Industry {
  _id: string;
  name: string;
  type: "industry" | "skills";
  createdAt: string;
  updatedAt: string;
}

// Establishment Types
export interface Establishment {
  _id: string;
  userId: string;
  serviceType: string;
  establishmentName: string;
  establishmentDetails: string;
  specialty?: string;
  registrationNumber?: string;
  yearOfExperience?: number;
  websiteLink?: string;
  certificates?: {
    fileType: string;
    fileUrl: string;
    size: number;
  }[];
  address?: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
  };
  radius?: number;
  coordinates?: {
    latitude: string | number;
    longitude: string | number;
  };
  establishmentLogo?: {
    fileType: string;
    fileUrl: string;
    size: number;
  };
  establishmentImages?: {
    fileType: string;
    fileUrl: string;
    size: number;
  }[];
  managerId?: string;
  createdAt: string;
  updatedAt: string;
}

// Staff Types
export interface Staff {
  _id: string;
  userId: string | {
    _id: string;
    fullName: string;
    email: string;
    phoneNumber?: string;
    profilePicture?: {
      fileUrl: string;
    };
    userStatus: number;
    role: number;
  };
  fullName?: string; // For convenience when userId is populated
  bio?: string;
  profilePicture?: {
    fileType: string;
    fileUrl: string;
    size: number;
  };
  experience?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
  };
  dateOfBirth?: string;
  gender?: string;
  industry?: string;
  designation?: string;
  skills?: string[];
  website?: string;
  linkedin?: string;
  instagram?: string;
  resume?: {
    name?: string;
    fileType: string;
    fileUrl: string;
    size: number;
  };
  certifications?: {
    name: string;
    fileType: string;
    fileUrl: string;
    size: number;
  }[];
  ssn?: string;
  hourlyRate?: number;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  createdAt: string;
  updatedAt: string;
}

// Establishment Owner with additional stats
export interface EstablishmentOwner extends User {
  establishments: Establishment[];
  establishmentCount: number;
  managerCount: number;
}

export interface TicketMessage {
  _id: string;
  ticketId: string;
  senderId: string;
  sender: User | Admin;
  message: string;
  isInternal: boolean; // Internal notes visible only to admins
  attachments?: {
    name: string;
    fileType: string;
    fileUrl: string;
    size: number;
  }[];
  createdAt: string;
}

// API Service Class
class ApiService {
  private api: AxiosInstance;
  private client: ApiClient;

  constructor() {
    this.client = apiClient;
    this.api = this.client.getInstance();
  }

  setAuthToken(token: string) {
    this.client.setAuthToken(token);
  }

  async logout(): Promise<ApiResponse> {
    try {
      const response = await this.api.post("/logout");
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        console.log("Axios error:", error.response?.data);
      }
      // Even if the API call fails, we should still clear local auth
      throw error as Error;
    } finally {
      // Always clear local auth state
      this.client.logout();
    }
  }

  // Auth Methods
  async login(
    email: string,
    password: string,
    pendingLoginToken?: string,
    twoFactorCode?: string,
    recoveryCode?: string
  ): Promise<ApiResponse<{
    accessToken?: string;
    user?: User;
    is_2fa_enabled?: boolean;
    pending_login_token?: string;
  }>> {
    const response = await this.api.post("/login", {
      email,
      password,
      role: 3, // admin role from user_type enum
      pending_login_token: pendingLoginToken,
      two_factor_code: twoFactorCode,
      recovery_code: recoveryCode,
    });
    return response.data;
  }

  async forgotPassword(email: string): Promise<ApiResponse<{ refId: string }>> {
    const response = await this.api.post("/forgot-password", { email });
    return response.data;
  }

  async verifyOTP(otp: string, uid: string, verifyType: number): Promise<ApiResponse> {
    const response = await this.api.post("/verify-otp", { otp, uid, verifyType });
    return response.data;
  }

  async resendOTP(uid: string): Promise<ApiResponse> {
    const response = await this.api.post("/resend-otp", { uid });
    return response.data;
  }

  async resetPassword(
    newPassword: string,
    uid: string,
    confirmPassword: string
  ): Promise<ApiResponse> {
    const response = await this.api.post("/reset-password", {
      newPassword,
      uid,
      confirmPassword,
    });
    return response.data;
  }

  // User Management
  async getAllUsers(params?: {
    page?: number;
    role?: number;
    status?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<PaginatedResponse<User>>> {
    const response = await this.api.get("/admin/users", { params });
    return response.data;
  }

  async getUserById(id: string): Promise<ApiResponse<User>> {
    const response = await this.api.get(`/admin/users/${id}`);
    return response.data;
  }

  async updateUser(id: string, data: {
    fullName?: string;
    email?: string;
    role?: number;
    userStatus?: number;
  }): Promise<ApiResponse<User>> {
    const response = await this.api.put(`/admin/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/admin/users/${id}`);
    return response.data;
  }

  // Admin Management
  async getAllAdmins(params?: { page?: number }): Promise<ApiResponse<PaginatedResponse<Admin>>> {
    const response = await this.api.get("/admin/admin", { params });
    return response.data;
  }

  async createAdmin(data: {
    firstName: string;
    lastName: string;
    email: string;
  }): Promise<ApiResponse<Admin>> {
    const response = await this.api.post("/admin/admin", data);
    return response.data;
  }

  async updateAdmin(id: string, data: Partial<Admin>): Promise<ApiResponse<Admin>> {
    const response = await this.api.put(`/admin/admin/${id}`, data);
    return response.data;
  }

  async deleteAdmin(id: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/admin/admin/${id}`);
    return response.data;
  }

  // FAQ Management
  async getAllFAQs(params?: { page?: number }): Promise<ApiResponse<PaginatedResponse<FAQ>>> {
    const response = await this.api.get("/admin/faq", { params });
    return response.data;
  }

  async createFAQ(data: { question: string; answer: string }): Promise<ApiResponse<FAQ>> {
    const response = await this.api.post("/admin/faq", data);
    return response.data;
  }

  async updateFAQ(
    id: string,
    data: { question: string; answer: string }
  ): Promise<ApiResponse<FAQ>> {
    const response = await this.api.put(`/admin/faq/${id}`, data);
    return response.data;
  }

  async deleteFAQ(id: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/admin/faq/${id}`);
    return response.data;
  }

  // Content Management
  async getAllContent(params?: { filter?: string }): Promise<ApiResponse<AppContent[]>> {
    const response = await this.api.get("/admin/content", { params });
    return response.data;
  }

  async createContent(data: { data: string; type: string }): Promise<ApiResponse> {
    const response = await this.api.patch("/admin/content", data);
    return response.data;
  }

  async updateContent(data: { data: string; type: string }): Promise<ApiResponse> {
    const response = await this.api.patch("/admin/content", data);
    return response.data;
  }

  async deleteContent(type: string): Promise<ApiResponse> {
    const response = await this.api.delete("/admin/content", { data: { type } });
    return response.data;
  }

  // Support Tickets Management
  async getAllSupportTickets(params?: {
    page?: number;
    status?: string;
    priority?: string;
    category?: string;
    assignedTo?: string;
  }): Promise<ApiResponse<PaginatedResponse<SupportTicket>>> {
    const response = await this.api.get("/admin/support/tickets", { params });
    return response.data;
  }

  async getSupportTicketById(ticketId: string): Promise<ApiResponse<SupportTicket>> {
    const response = await this.api.get(`/admin/support/tickets/${ticketId}`);
    return response.data;
  }

  async updateSupportTicket(
    ticketId: string,
    data: {
      status?: string;
      priority?: string;
      assignedTo?: string;
    }
  ): Promise<ApiResponse<SupportTicket>> {
    const response = await this.api.patch(`/admin/support/tickets/${ticketId}`, data);
    return response.data;
  }

  async getTicketMessages(ticketId: string): Promise<ApiResponse<SupportMessage[]>> {
    const response = await this.api.get(`/admin/support/tickets/${ticketId}/messages`);
    return response.data;
  }

  async addTicketMessage(
    ticketId: string,
    data: {
      message: string;
      isInternal?: boolean;
      attachments?: any[];
    }
  ): Promise<ApiResponse<SupportMessage>> {
    const response = await this.api.post(`/admin/support/tickets/${ticketId}/messages`, data);
    return response.data;
  }

  async getSupportStats(): Promise<ApiResponse<SupportStats>> {
    const response = await this.api.get("/admin/support/stats");
    return response.data;
  }

  // Notification Management
  async getAllNotifications(params?: {
    page?: number;
  }): Promise<ApiResponse<PaginatedResponse<Notification>>> {
    const response = await this.api.get("/admin/notifications", { params });
    return response.data;
  }

  async createNotification(data: {
    title: string;
    message: string;
    type: string;
    targetUsers: string[];
  }): Promise<ApiResponse<Notification>> {
    const response = await this.api.post("/admin/notifications", data);
    return response.data;
  }

  async updateNotification(
    id: string,
    data: Partial<Notification>
  ): Promise<ApiResponse<Notification>> {
    const response = await this.api.patch(`/admin/notifications/${id}`, data);
    return response.data;
  }

  async deleteNotification(id: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/admin/notifications/${id}`);
    return response.data;
  }

  async updateNotificationPreferences(data: {
    notifications: {
      email: boolean;
      push: boolean;
      sms: boolean;
      system?: boolean;
      user?: boolean;
      admin?: boolean;
      alert?: boolean;
    };
  }): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/admin/settings/preferences", data);
    return response.data;
  }

  async sendNotification(data: {
    title: string;
    message: string;
    type: string;
    targetUsers: string[];
  }): Promise<ApiResponse> {
    const response = await this.api.post("/admin/send-notification", data);
    return response.data;
  }

  // Notification Templates
  async getAllTemplates(params?: {
    page?: number;
  }): Promise<ApiResponse<PaginatedResponse<NotificationTemplate>>> {
    const response = await this.api.get("/admin/notification-templates", { params });
    return response.data;
  }

  async createTemplate(data: {
    title: string;
    body: string;
    category?: string;
  }): Promise<ApiResponse<NotificationTemplate>> {
    const response = await this.api.post("/admin/notification-templates", data);
    return response.data;
  }

  async updateTemplate(
    id: string,
    data: Partial<NotificationTemplate>
  ): Promise<ApiResponse<NotificationTemplate>> {
    const response = await this.api.patch(`/admin/notification-templates/${id}`, data);
    return response.data;
  }

  async deleteTemplate(id: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/admin/notification-templates/${id}`);
    return response.data;
  }

  // Notification Groups
  async getAllGroups(params?: {
    page?: number;
  }): Promise<ApiResponse<PaginatedResponse<NotificationGroup>>> {
    const response = await this.api.get("/admin/notification-group", { params });
    return response.data;
  }

  async createGroup(
    data:
      | FormData
      | {
          userIds: string[];
          groupName: string;
        }
  ): Promise<ApiResponse<NotificationGroup>> {
    const response = await this.api.post("/admin/notification-group", data);
    return response.data;
  }

  async updateGroup(
    id: string,
    data: FormData | Partial<NotificationGroup>
  ): Promise<ApiResponse<NotificationGroup>> {
    const response = await this.api.patch(`/admin/notification-group/${id}`, data);
    return response.data;
  }

  async deleteGroup(id: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/admin/notification-group/${id}`);
    return response.data;
  }

  async getGroupUsers(id: string): Promise<ApiResponse<User[]>> {
    const response = await this.api.get(`/admin/notification-group/${id}`);
    return response.data;
  }

  // Statistics
  async getGraphData(year?: string): Promise<ApiResponse<GraphData[]>> {
    const response = await this.api.get(`/admin/graph-figure/${year ?? new Date().getFullYear()}`);
    return response.data;
  }

  // Industry Management
  async updateIndustry(data: {
    industry: string;
    type: "industry" | "skills";
    id?: string;
  }): Promise<ApiResponse<Industry>> {
    const response = await this.api.patch("/admin/industry", data);
    return response.data;
  }

  async deleteIndustry(id: string): Promise<ApiResponse<Industry>> {
    const response = await this.api.delete(`/admin/industry/${id}`);
    return response.data;
  }

  // Establishment Management
  async getAllEstablishments(params?: {
    page?: number;
    filter?: string;
  }): Promise<ApiResponse<PaginatedResponse<Establishment>>> {
    const response = await this.api.get("/admin/establishments", { params });
    return response.data;
  }

  async getEstablishmentById(id: string): Promise<ApiResponse<Establishment>> {
    const response = await this.api.get(`/admin/establishments/${id}`);
    return response.data;
  }

  async deleteEstablishment(id: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/admin/establishments/${id}`);
    return response.data;
  }

  // Staff Management
  async getAllStaff(params?: {
    page?: number;
    filter?: string;
  }): Promise<ApiResponse<PaginatedResponse<Staff>>> {
    const response = await this.api.get("/admin/staff", { params });
    return response.data;
  }

  async getStaffById(id: string): Promise<ApiResponse<Staff>> {
    const response = await this.api.get(`/admin/staff/${id}`);
    return response.data;
  }

  async deleteStaff(id: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/admin/staff/${id}`);
    return response.data;
  }

  // Managers
  async getAllManagers(params?: {
    page?: number;
    filter?: string;
  }): Promise<ApiResponse<PaginatedResponse<Manager>>> {
    const response = await this.api.get("/admin/managers", { params });
    return response.data;
  }

  async deleteManager(id: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/admin/managers/${id}`);
    return response.data;
  }

  // Get managers for a specific establishment
  async getManagersByEstablishmentId(establishmentId: string): Promise<ApiResponse<Manager[]>> {
    const response = await this.api.get(`/admin/establishments/${establishmentId}/managers`);
    return response.data;
  }

  // Dashboard & Analytics
  async getDashboardStats(): Promise<ApiResponse<{
    overview: {
      totalUsers: number;
      totalEstablishments: number;
      totalJobs: number;
      totalApplications: number;
      totalRevenue: number;
      activeUsers: number;
      userGrowth: number;
      revenueGrowth: number;
      jobGrowth: number;
    };
    recentActivity: {
      recentUsers: Array<{
        id: string;
        name: string;
        email: string;
        createdAt: string;
        userType: number;
      }>;
    };
    charts: {
      monthlyUsers: Array<{
        month: string;
        users: number;
      }>;
    };
    support: {
      total: number;
      open: number;
      inProgress: number;
      resolved: number;
      closed: number;
    };
    subscriptions: any;
    activities: any[];
  }>> {
    const response = await this.api.get("/admin/dashboard");
    return response.data;
  }

  // Establishment Owners Management
  async getAllEstablishmentOwners(params?: {
    page?: number;
    filter?: string;
  }): Promise<ApiResponse<PaginatedResponse<EstablishmentOwner>>> {
    const response = await this.api.get("/admin/establishment-owners", { params });
    return response.data;
  }

  async getEstablishmentOwnerById(id: string): Promise<ApiResponse<EstablishmentOwner>> {
    const response = await this.api.get(`/admin/establishment-owners/${id}`);
    return response.data;
  }

  async deleteEstablishmentOwner(id: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/admin/establishment-owners/${id}`);
    return response.data;
  }

  // Subscription Management
  async getAllSubscriptions(): Promise<ApiResponse<SubscriptionPlan[]>> {
    const response = await this.api.get("/admin/subscription");
    return response.data;
  }

  async getSubscriptionById(id: string): Promise<ApiResponse<SubscriptionPlan>> {
    const response = await this.api.get(`/admin/subscription/${id}`);
    return response.data;
  }

  async createSubscription(data: {
    name: string;
    discountPriceTag: number;
    price: number;
    maxGigs: number;
    benefits: string[];
  }): Promise<ApiResponse<SubscriptionPlan>> {
    // Backend expects benefits as JSON string
    const requestData = {
      ...data,
      benefits: JSON.stringify(data.benefits),
    };
    const response = await this.api.post("/admin/subscription", requestData);
    return response.data;
  }

  async updateSubscription(
    id: string,
    data: {
      name?: string;
      discountPriceTag?: number;
      price?: number;
      maxGigs?: number;
      benefits?: string[];
    }
  ): Promise<ApiResponse<SubscriptionPlan>> {
    // Backend expects benefits as JSON string
    const requestData = {
      ...data,
      benefits: data.benefits ? JSON.stringify(data.benefits) : undefined,
    };
    const response = await this.api.patch(`/admin/subscription/${id}`, requestData);
    return response.data;
  }

  async deleteSubscription(id: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/admin/subscription/${id}`);
    return response.data;
  }

  async getSubscriptionAnalytics(): Promise<ApiResponse<SubscriptionAnalytics>> {
    const response = await this.api.get("/admin/subscription-analytics");
    return response.data;
  }

  // Admin Dashboard
  async getAdminDashboard(): Promise<ApiResponse<AdminDashboardData>> {
    const response = await this.api.get("/admin/dashboard");
    return response.data;
  }

  // System Health Check
  async getSystemHealth(): Promise<ApiResponse<{
    server: string;
    mongodb: string;
    redis: string;
    timestamp: string;
    responseTimes: {
      total: number;
      mongodb: number;
      redis: number;
    };
  }>> {
    // Health endpoint is at root level, not under /api/v1
    // Extract the base server URL without the /api/v1 prefix
    const baseServerUrl = config.api.baseUrl.replace(/\/api\/v1\/?$/, '');
    const response = await axios.get(`${baseServerUrl}/health`);
    return response.data;
  }

  // User Analytics
  async getUserAnalytics(params?: {
    timeRange?: string;
    userType?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.get("/admin/user-analytics", { params });
    return response.data;
  }

  // Payment Processing
  async getAllTransactions(params?: {
    page?: number;
    filter?: string;
    status?: string;
    type?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<PaginatedResponse<any>>> {
    const response = await this.api.get("/admin/transactions", { params });
    return response.data;
  }

  async getTransactionById(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/admin/transactions/${id}`);
    return response.data;
  }

  async updateTransactionStatus(id: string, status: string): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/admin/transactions/${id}`, { status });
    return response.data;
  }

  async getAllRefundRequests(params?: {
    page?: number;
    filter?: string;
    status?: string;
  }): Promise<ApiResponse<PaginatedResponse<any>>> {
    const response = await this.api.get("/admin/refund-requests", { params });
    return response.data;
  }

  async createRefundRequest(data: {
    transactionId: string;
    amount: number;
    reason: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post("/admin/refund-requests", data);
    return response.data;
  }

  async updateRefundRequestStatus(id: string, status: string): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/admin/refund-requests/${id}`, { status });
    return response.data;
  }

  async getAllEarnings(params?: { page?: number }): Promise<ApiResponse<PaginatedResponse<any>>> {
    const response = await this.api.get("/admin/earnings", { params });
    return response.data;
  }

  async getEarningsById(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/admin/earnings/${id}`);
    return response.data;
  }

  async getPaymentMetrics(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/admin/payment-metrics");
    return response.data;
  }

  async getTransactionAnalytics(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/admin/transaction-analytics");
    return response.data;
  }

  async getEarningsAnalytics(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/admin/earnings-analytics");
    return response.data;
  }

  // Debug authentication
  async debugAuth(): Promise<ApiResponse<any>> {
    console.log("API Service: debugAuth called");
    console.log("API Service: Current auth token:", this.client.getAuthToken());
    console.log(
      "API Service: Authorization header:",
      this.api.defaults.headers.common.Authorization
    );
    const response = await this.api.get("/admin/debug-auth");
    return response.data;
  }

  // Admin Settings
  async getAdminSettings(): Promise<ApiResponse<any>> {
    console.log("API Service: getAdminSettings called");
    console.log("API Service: Current auth token:", this.client.getAuthToken());
    console.log(
      "API Service: Authorization header:",
      this.api.defaults.headers.common.Authorization
    );
    const response = await this.api.get("/admin/settings");
    return response.data;
  }

  async getCurrentAdminUser(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/admin/settings/user");
    return response.data;
  }

  async updateProfileSettings(data: {
    fullName?: string;
    email?: string;
    phone?: string;
    bio?: string;
    timezone?: string;
    language?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/admin/settings/profile", data);
    return response.data;
  }

  async updateSecuritySettings(data: {
    twoFactorAuth?: boolean;
    sessionTimeout?: string;
    passwordExpiry?: string;
    loginNotifications?: boolean;
    suspiciousActivityAlerts?: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/admin/settings/security", data);
    return response.data;
  }

  async updateSystemSettings(data: {
    maintenanceMode?: boolean;
    debugMode?: boolean;
    logRetention?: string;
    maxFileUploadSize?: string;
    emailNotifications?: boolean;
    pushNotifications?: boolean;
    autoBackup?: boolean;
    backupFrequency?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/admin/settings/system", data);
    return response.data;
  }

  async changePassword(data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/admin/settings/change-password", data);
    return response.data;
  }

  async resetFailedLoginAttempts(): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/admin/settings/reset-login-attempts");
    return response.data;
  }

  async checkAccountLockStatus(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/admin/settings/account-lock-status");
    return response.data;
  }

  async exportSettings(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/admin/settings/export");
    return response.data;
  }

  async updatePreferences(data: {
    theme?: string;
    language?: string;
    timezone?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/admin/settings/preferences", data);
    return response.data;
  }

  async getAllAdminSettings(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/admin/settings/all");
    return response.data;
  }

  async getAdminSettingsById(adminId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/admin/settings/${adminId}`);
    return response.data;
  }

  async updateAdminSettingsById(adminId: string, data: any): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/admin/settings/${adminId}`, data);
    return response.data;
  }

  // Badge Count Methods
  async getUnreadNotificationCount(): Promise<ApiResponse<{ count: number }>> {
    const response = await this.api.get("/notification/unread-count");
    return response.data;
  }

  async getSupportTicketCounts(): Promise<ApiResponse<SupportStats>> {
    const response = await this.api.get("/admin/support/stats");
    return response.data;
  }

  async getBadgeCounts(): Promise<{
    notifications: number;
    supportTickets: number;
    totalPending: number;
  }> {
    try {
      const [notificationResponse, supportResponse] = await Promise.allSettled([
        this.getUnreadNotificationCount(),
        this.getSupportTicketCounts(),
      ]);

      const notifications =
        notificationResponse.status === "fulfilled" && notificationResponse.value.type === 0
          ? notificationResponse.value.data.count
          : 0;

      const supportTickets =
        supportResponse.status === "fulfilled" && supportResponse.value.type === 0
          ? supportResponse.value.data.open + supportResponse.value.data.inProgress
          : 0;

      return {
        notifications,
        supportTickets,
        totalPending: notifications + supportTickets,
      };
    } catch (error) {
      console.error("Error fetching badge counts:", error);
      return {
        notifications: 0,
        supportTickets: 0,
        totalPending: 0,
      };
    }
  }

  // Two-Factor Authentication Methods
  async enableTwoFactor(userId: string): Promise<ApiResponse<{
    secret: string;
    otp_auth_url: string;
    qr_code: string;
    recovery_codes: string[];
  }>> {
    const response = await this.api.post("/2fa/enable", { user_id: userId });
    return response.data;
  }

  async verifyTwoFactor(userId: string, code: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await this.api.post("/2fa/verify", { user_id: userId, code });
    return response.data;
  }

  async disableTwoFactor(userId: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await this.api.post("/2fa/disable", { user_id: userId });
    return response.data;
  }

  async getTwoFactorStatus(userId: string): Promise<ApiResponse<{ enabled: boolean; is_2fa_enabled: boolean }>> {
    const response = await this.api.get(`/2fa/status/${userId}`);
    return response.data;
  }

  // Login with 2FA support
  async loginWith2FA(
    email: string,
    password: string,
    pendingLoginToken?: string,
    twoFactorCode?: string,
    recoveryCode?: string
  ): Promise<ApiResponse<{
    accessToken?: string;
    user?: User;
    is_2fa_enabled?: boolean;
    pending_login_token?: string;
  }>> {
    const response = await this.api.post("/login", {
      email,
      password,
      role: 3, // admin role
      pending_login_token: pendingLoginToken,
      two_factor_code: twoFactorCode,
      recovery_code: recoveryCode,
    });
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
